/**
 * Durable marker for "an instalment plan is in flight".
 *
 * `paymentGatewaysState.isSplitPayment` lives in memory only, so it is already
 * false by the time a full-page 3DS return re-mounts the checkout. The guards
 * that keep a partly paid order away from the confirmation page (BEC-24895)
 * therefore need a signal that survives the page load — scoped to the checkout
 * or order being paid so a leftover marker cannot attach to a different one.
 */

const SPLIT_PAYMENT_IN_FLIGHT_KEY = 'checkout.splitPaymentInFlight';

interface SplitPaymentInFlightMarker {
  orderId: string;
}

function getSessionStorage(): Storage | null {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    // Safari throws on storage access in some privacy modes.
    return null;
  }
}

function readMarker(): SplitPaymentInFlightMarker | null {
  const storage = getSessionStorage();
  if (!storage) return null;

  const raw = storage.getItem(SPLIT_PAYMENT_IN_FLIGHT_KEY);
  if (!raw) return null;

  // Legacy tab-wide marker from the first BEC-24895 pass — treat as absent.
  if (raw === '1') {
    storage.removeItem(SPLIT_PAYMENT_IN_FLIGHT_KEY);
    return null;
  }

  try {
    const marker = JSON.parse(raw) as SplitPaymentInFlightMarker;
    if (!marker.orderId) {
      storage.removeItem(SPLIT_PAYMENT_IN_FLIGHT_KEY);
      return null;
    }
    return marker;
  } catch {
    storage.removeItem(SPLIT_PAYMENT_IN_FLIGHT_KEY);
    return null;
  }
}

/**
 * Record that the shopper is paying a specific checkout or order in instalments.
 *
 * Example Input:
 * 'T3JkZXI6MTIz'
 *
 * Example Output:
 * undefined (sessionStorage now holds {"orderId":"T3JkZXI6MTIz"})
 */
export function setSplitPaymentInFlight(orderId: string): void {
  const storage = getSessionStorage();
  if (!storage || !orderId) return;

  const marker: SplitPaymentInFlightMarker = { orderId };
  storage.setItem(SPLIT_PAYMENT_IN_FLIGHT_KEY, JSON.stringify(marker));
}

/**
 * Remove the persisted instalment marker for the current tab.
 *
 * Example Input:
 * (none)
 *
 * Example Output:
 * undefined (sessionStorage key removed)
 */
export function clearSplitPaymentInFlight(): void {
  getSessionStorage()?.removeItem(SPLIT_PAYMENT_IN_FLIGHT_KEY);
}

/**
 * True when an instalment plan is unfinished for the given checkout or order.
 *
 * Example Input:
 * 'T3JkZXI6MTIz', marker holds the same id
 *
 * Example Output:
 * true
 */
export function isSplitPaymentInFlight(orderId?: string | null): boolean {
  if (!orderId) return false;

  const marker = readMarker();
  return marker?.orderId === orderId;
}
